/**
 * KAN-951: content of the "Помощь" section.
 *
 * Data only: content.json is generated by the tutorial recording tool from
 * docs/tutorials/scripts/*.yaml. Order of items = order of display.
 * The video file lives next to the KAN-950 tutorials (buildVideoUrl).
 */
import content from './content.json';
import { buildVideoUrl, hiddenTutorialSlugs, TUTORIALS_BASE_URL } from './registry';

export type HelpRole = 'owner' | 'manager';

export interface HelpStep {
  say: string;
  /** Who sees this step in the text; not set = everyone who sees the item. */
  roles?: HelpRole[];
}

export interface HelpItem {
  slug: string;
  /** Cabinet page the video is about. */
  route: string;
  title: string;
  roles: HelpRole[];
  /** Video length, seconds. */
  duration: number;
  /** Video file name, e.g. "clients.mp4". */
  video: string;
  intro: string;
  steps: HelpStep[];
  outro: string;
}

export interface HelpContent {
  generated_at: string;
  items: HelpItem[];
}

const HELP_CONTENT = content as unknown as HelpContent;

/** Items of content.json minus videos hidden in the registry (hidden: true). */
export function visibleHelpItems(
  items: HelpItem[],
  hidden: Set<string> = hiddenTutorialSlugs()
): HelpItem[] {
  return items.filter((item) => !hidden.has(item.slug));
}

export const HELP_ITEMS: HelpItem[] = visibleHelpItems(HELP_CONTENT.items);

/** Owner sees everything, a manager only items marked for managers. */
export function helpItemsForRole(role: HelpRole, items: HelpItem[] = HELP_ITEMS): HelpItem[] {
  if (role === 'owner') return items;
  return items.filter((item) => item.roles.includes(role));
}

/** Roles of the cabinet that see the whole help (owner and operator). */
const FULL_HELP_ROLES: ReadonlySet<string> = new Set(['OWNER', 'USER']);

/**
 * Owner and operator see everything; the manager login, an unknown role and a
 * user not loaded yet get the smaller manager part.
 */
export function helpRoleForUser(user: { role?: string } | null | undefined): HelpRole {
  return user?.role && FULL_HELP_ROLES.has(user.role) ? 'owner' : 'manager';
}

/** Steps of the item shown to the role (steps without roles are for everyone). */
export function stepsForRole(item: HelpItem, role: HelpRole): HelpStep[] {
  if (role === 'owner') return item.steps;
  return item.steps.filter((step) => !step.roles || step.roles.includes(role));
}

export function findHelpItem(slug: string | undefined, items: HelpItem[] = HELP_ITEMS): HelpItem | null {
  if (!slug) return null;
  return items.find((item) => item.slug === slug) ?? null;
}

export function videoUrlFor(item: HelpItem, baseUrl: string = TUTORIALS_BASE_URL): string {
  return buildVideoUrl(item.video.replace(/\.mp4$/i, ''), baseUrl);
}

/** Seconds -> "мм:сс", rounded to whole seconds. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(total / 60);
  const rest = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}

/**
 * Case-insensitive search by title, intro and step texts. With a role, only
 * the steps that role sees are searched. Empty query = all items.
 */
export function searchHelpItems(items: HelpItem[], query: string, role?: HelpRole): HelpItem[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return items;
  return items.filter((item) => {
    const steps = role ? stepsForRole(item, role) : item.steps;
    const haystack = [item.title, item.intro, ...steps.map((step) => step.say)];
    return haystack.some((text) => text.toLowerCase().includes(needle));
  });
}
